import React, { useState, useEffect } from 'react';
import { Settings, Save, X } from 'lucide-react';

interface ReportSettingsProps {
  onClose: () => void;
}

const ReportSettings: React.FC<ReportSettingsProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    shop_name: '和とん',
    owner_name: 'カツ氏',
    advisor_name: 'ニニギ',
    advisor_character: 'あなたは和とんの応援者「ニニギ」です。店主を励まし、前向きな気持ちにさせる存在です。語尾は「にゃん」で、親しみやすく温かい言葉をかけます。',
    advisor_suffix: 'にゃん',
    owner_profile: 'とんかつ屋「和とん」の店主。毎日一生懸命働いている。'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch('/api/report-settings');
      const data = await res.json();
      if (data.success) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('設定取得エラー:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/report-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      const data = await res.json();
      if (data.success) {
        alert('設定を保存しました！');
        onClose();
      } else {
        alert('保存に失敗しました: ' + data.error);
      }
    } catch (error) {
      console.error('設定保存エラー:', error);
      alert('エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="text-orange-500" />
            日報設定
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* 店舗情報 */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-bold text-lg mb-4 text-blue-700">🏪 店舗情報</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  店舗名
                </label>
                <input
                  type="text"
                  value={settings.shop_name}
                  onChange={(e) => setSettings({ ...settings, shop_name: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  オーナー名（日報に表示される名前）
                </label>
                <input
                  type="text"
                  value={settings.owner_name}
                  onChange={(e) => setSettings({ ...settings, owner_name: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  オーナープロフィール（AIが参考にする情報）
                </label>
                <textarea
                  value={settings.owner_profile}
                  onChange={(e) => setSettings({ ...settings, owner_profile: e.target.value })}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="例: とんかつ屋「和とん」の店主。40代。家族経営で毎日頑張っている。"
                />
              </div>
            </div>
          </div>

          {/* アドバイザー設定 */}
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="font-bold text-lg mb-4 text-purple-700">💫 アドバイザー設定</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  アドバイザー名
                </label>
                <input
                  type="text"
                  value={settings.advisor_name}
                  onChange={(e) => setSettings({ ...settings, advisor_name: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="例: ニニギ"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  語尾
                </label>
                <input
                  type="text"
                  value={settings.advisor_suffix}
                  onChange={(e) => setSettings({ ...settings, advisor_suffix: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="例: にゃん"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  キャラクター設定（AIの性格・役割）
                </label>
                <textarea
                  value={settings.advisor_character}
                  onChange={(e) => setSettings({ ...settings, advisor_character: e.target.value })}
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="例: あなたは和とんの応援者「ニニギ」です。店主を励まし、前向きな気持ちにさせる存在です。"
                />
              </div>
            </div>
          </div>

          {/* プレビュー */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-bold text-sm mb-2 text-gray-700">📝 プレビュー</h3>
            <p className="text-sm text-gray-600">
              日報タイトル: <span className="font-bold">🍖 {settings.shop_name}日報</span>
            </p>
            <p className="text-sm text-gray-600 mt-2">
              振り返りセクション: <span className="font-bold">💭 今日の振り返り（{settings.owner_name}の心情）</span>
            </p>
            <p className="text-sm text-gray-600 mt-2">
              アドバイザー: <span className="font-bold">💫 {settings.advisor_name}からの一言（語尾: {settings.advisor_suffix}）</span>
            </p>
          </div>
        </div>

        {/* 保存ボタン */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex gap-3">
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-lg font-semibold
                     hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2"
          >
            <Save size={20} />
            {loading ? '保存中...' : '設定を保存'}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
          >
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportSettings;
